import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Anchor, Badge, Button, Center, Checkbox, Group, Loader, Menu, SegmentedControl, Select, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { TaskStatus } from '@pst/shared';
import { ApiError, agendaApi, scheduleApi, type AgendaProject, undoPaths } from '../../api';
import { TASK_STATUS, UNIT_DAYS, formatDate, todayIso } from '../../format';
import { PageHeader, Section } from '../../ui/Section';
import { guardedStep, useUndo } from '../../ui/undo';
import { BUCKETS, type TodoItem, bucketOf, flatten } from './agenda';
import { linkTo, openInNewTab } from '../../ui/links';
import { useContextMenu } from '../../ui/ContextMenu';
import { TaskCommentsModal } from './TaskComments';
import { TaskEditModal, type TaskEditTarget } from './TaskEditModal';
import { inverseTaskBody } from './taskEdit';
import { DateCell } from './DateCell';
import { NumberCell } from './NumberCell';
import { EditableText } from '../../ui/EditableText';

const PREFS = 'pst.tasks.prefs';

/** A task not done whose planned end lies after its "Spätestens" - as the plan marks it too. */
const missesDeadline = (t: { status: string; deadline: string | null; finish: string | null }): boolean =>
  t.status !== 'done' && !!t.deadline && !!t.finish && t.finish > t.deadline;
type Prefs = { who: 'mine' | 'all'; projectId: string | null; showDone: boolean };
const loadPrefs = (): Prefs => {
  try { return { who: 'mine', projectId: null, showDone: false, ...(JSON.parse(window.localStorage.getItem(PREFS) ?? '{}') as Partial<Prefs>) }; } catch { return { who: 'mine', projectId: null, showDone: false }; }
};
const savePrefs = (p: Prefs): void => { try { window.localStorage.setItem(PREFS, JSON.stringify(p)); } catch { /* private window */ } };

/**
 * The schedule as a to-do list: every task of every visible project, sorted
 * into overdue, today, this week, the next two weeks and later. Ticking a
 * task off sets it to "erledigt" in the schedule itself - one plan, two views.
 */
export function TasksPage({ projectId: fixed }: { projectId?: string } = {}): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const undo = useUndo();
  const me = queryClient.getQueryData<{ permissions: string[]; ownerCodes: string[] }>(['me']);
  const canEdit = me?.permissions.includes('scheduling.write') ?? false;
  const codes = me?.ownerCodes ?? [];
  const data = useQuery({ queryKey: ['agenda'], queryFn: agendaApi.list });
  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs);
  const setPrefs = (p: Partial<Prefs>): void => { const next = { ...prefs, ...p }; setPrefsState(next); savePrefs(next); };
  const [search, setSearch] = useState('');
  const setStatus = useMutation({
    mutationFn: ({ item, status }: { item: TodoItem; status: TaskStatus }) => {
      const back = item.status === 'done' && item.doneOn ? { status: item.status, doneOn: item.doneOn } : { status: item.status };
      const step = guardedStep(`Aufgabe ${item.code}`, 'PATCH', undoPaths.task(item.projectId, item.id), back, { status });
      return undo.recordAfter(scheduleApi.updateTask(item.projectId, item.id, { status }), step);
    },
    // The tick shows at once; a refusal puts the list back.
    onMutate: async ({ item, status }) => {
      await queryClient.cancelQueries({ queryKey: ['agenda'] });
      const previous = queryClient.getQueryData<AgendaProject[]>(['agenda']);
      queryClient.setQueryData<AgendaProject[]>(['agenda'], (old) =>
        old?.map((p) => (p.projectId !== item.projectId ? p : { ...p, tasks: p.tasks.map((t) => (t.id === item.id ? { ...t, status } : t)) })),
      );
      return { previous };
    },
    onError: (_e, _v, context) => { if (context?.previous) queryClient.setQueryData(['agenda'], context.previous); },
    onSettled: async (_r, _e, v) => {
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      await queryClient.invalidateQueries({ queryKey: ['schedule', v.item.projectId] });
      await queryClient.invalidateQueries({ queryKey: ['schedule-overview'] });
    },
  });

  // A change from a cell: saved, then undoable with the values the server stored (ADR 0008).
  const edit = useMutation({
    mutationFn: async ({ item, body }: { item: TodoItem; body: Record<string, unknown> }) => {
      const back = inverseTaskBody({ ...item, constraintType: item.startFixed ? 'startNoEarlierThan' : null, constraintDate: item.startFixed }, body);
      const saved = await scheduleApi.updateTask(item.projectId, item.id, body);
      undo.record(guardedStep(`Aufgabe ${item.code}`, 'PATCH', undoPaths.task(item.projectId, item.id), back, saved as unknown as Record<string, unknown>));
    },
    onSettled: async (_r, _e, v) => {
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      await queryClient.invalidateQueries({ queryKey: ['schedule', v.item.projectId] });
      await queryClient.invalidateQueries({ queryKey: ['schedule-overview'] });
    },
  });
  const [editTarget, setEditTarget] = useState<TaskEditTarget | null>(null);

  const remove = useMutation({
    mutationFn: (item: TodoItem) => scheduleApi.deleteTask(item.projectId, item.id),
    onSettled: async (_r, _e, item) => {
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      await queryClient.invalidateQueries({ queryKey: ['schedule', item.projectId] });
      await queryClient.invalidateQueries({ queryKey: ['schedule-overview'] });
    },
  });
  const askRemove = (t: TodoItem): void => {
    if (window.confirm(`Vorgang ${t.code} „${t.name}“ in ${t.projectNumber} löschen? Er verschwindet auch aus dem Terminplan, samt seinen Anordnungsbeziehungen. Zurück nur über einen Snapshot des Projekts.`)) remove.mutate(t);
  };
  const [commentTask, setCommentTask] = useState<TodoItem | null>(null);
  const rowMenu = useContextMenu<TodoItem>((t, close) => {
    const run = (f: () => void) => () => { close(); f(); };
    return (
      <>
        <Menu.Label>{t.projectNumber} · {t.code}</Menu.Label>
        <Menu.Item onClick={run(() => navigate(`/projekte/${t.projectId}/termine`))}>Im Terminplan öffnen</Menu.Item>
        <Menu.Item onClick={run(() => openInNewTab(`/projekte/${t.projectId}/termine`))}>Terminplan in neuem Tab</Menu.Item>
        <Menu.Item onClick={run(() => setCommentTask(t))}>Kommentare …</Menu.Item>
        {canEdit && (
          <>
            <Menu.Item onClick={run(() => setEditTarget({ mode: 'edit', projectId: t.projectId, taskId: t.id }))}>Bearbeiten …</Menu.Item>
            <Menu.Item onClick={run(() => setEditTarget({ mode: 'create', projectId: t.projectId }))}>Neuer Vorgang in {t.projectNumber} …</Menu.Item>
            <Menu.Divider />
            <Menu.Label>Status</Menu.Label>
            {(Object.keys(TASK_STATUS) as TaskStatus[]).map((k) => (
              <Menu.Item key={k} disabled={k === t.status} onClick={run(() => setStatus.mutate({ item: t, status: k }))}>{TASK_STATUS[k].label}</Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Item color="red" onClick={run(() => askRemove(t))}>Vorgang löschen …</Menu.Item>
          </>
        )}
      </>
    );
  });

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Die Aufgaben konnten nicht geladen werden.</Alert>;
  const today = todayIso();
  const needle = search.trim().toLowerCase();
  const all = flatten(data.data);
  const items = all
    .filter((t) => (prefs.who === 'all' || t.mine))
    .filter((t) => (fixed ? t.projectId === fixed : !prefs.projectId || t.projectId === prefs.projectId))
    .filter((t) => !needle || `${t.code} ${t.name} ${t.owner ?? ''} ${t.projectNumber}`.toLowerCase().includes(needle))
    .sort((a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999') || a.projectNumber.localeCompare(b.projectNumber) || a.code.localeCompare(b.code, 'de', { numeric: true }));
  const grouped = BUCKETS.filter((b) => b.key !== 'done' || prefs.showDone).map((b) => ({ ...b, items: items.filter((t) => bucketOf(t, today) === b.key) }));
  const openCount = items.filter((t) => t.status !== 'done').length;

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        {!fixed && <PageHeader title="Aufgaben – alle Projekte" description="Die Vorgänge aller Terminpläne als To-do-Liste. Abhaken setzt den Vorgang im Terminplan auf „erledigt“; Titel, Termine, Dauer und Verantwortliche per Klick ändern; Rechtsklick auf eine Zeile: Bearbeiten, Status, Terminplan öffnen, Kommentare, Vorgang löschen." />}
        {edit.error && <Alert color="red" withCloseButton onClose={() => edit.reset()}>{edit.error instanceof ApiError ? edit.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        {setStatus.error && <Alert color="red" withCloseButton onClose={() => setStatus.reset()}>{setStatus.error instanceof ApiError ? setStatus.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        {remove.error && <Alert color="red" withCloseButton onClose={() => remove.reset()}>{remove.error instanceof ApiError ? remove.error.message : 'Der Vorgang konnte nicht gelöscht werden.'}</Alert>}
        {prefs.who === 'mine' && codes.length === 0 && (
          <Alert color="yellow" py={6}>
            Für „Meine“ braucht PST Ihr Kürzel aus der Spalte „Verantwortlich“ (z. B. „A“): Kontomenü oben rechts → Profil → „Meine Kürzel im Terminplan“.
          </Alert>
        )}
        <Group gap="sm" wrap="wrap">
          <SegmentedControl value={prefs.who} onChange={(v) => setPrefs({ who: v as Prefs['who'] })} data={[{ value: 'mine', label: `Meine${codes.length ? ` (${codes.join(', ')})` : ''}` }, { value: 'all', label: 'Alle' }]} />
          {!fixed && (
            <Select
              placeholder="alle Projekte" clearable w={280} value={prefs.projectId} onChange={(v) => setPrefs({ projectId: v })}
              data={data.data.map((p) => ({ value: p.projectId, label: `${p.number} – ${p.name}` }))}
            />
          )}
          <TextInput placeholder="Suchen: Nummer, Titel, Kürzel" value={search} onChange={(e) => setSearch(e.currentTarget.value)} w={260} />
          <Checkbox label="Erledigte zeigen" checked={prefs.showDone} onChange={(e) => setPrefs({ showDone: e.currentTarget.checked })} />
          <Text size="sm" c="dimmed">{openCount} offen</Text>
          {canEdit && <Button size="xs" variant="light" ml="auto" onClick={() => setEditTarget({ mode: 'create', projectId: fixed ?? prefs.projectId })}>+ Vorgang</Button>}
        </Group>

        {grouped.filter((g) => g.items.length > 0).map((g) => (
          <Section key={g.key} title={`${g.label} (${g.items.length})`}>
            <Table.ScrollContainer minWidth={900}>
              <Table verticalSpacing={3} className="pst-tabular" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={36} />
                    <Table.Th w={110}>Projekt</Table.Th>
                    <Table.Th w={70}>Nr.</Table.Th>
                    <Table.Th>Aufgabe</Table.Th>
                    <Table.Th w={110}>Beginn</Table.Th>
                    <Table.Th w={70} ta="right">Dauer</Table.Th>
                    <Table.Th w={110}>Ende</Table.Th>
                    <Table.Th w={110}>Spätestens</Table.Th>
                    <Table.Th w={100}>Verantw.</Table.Th>
                    <Table.Th w={110}>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {g.items.map((t) => {
                    const late = t.status !== 'done' && t.due !== null && t.due < today;
                    return (
                      <Table.Tr key={t.id} onContextMenu={(e) => rowMenu.open(e, t)}>
                        <Table.Td>
                          <Checkbox
                            aria-label={`${t.code} erledigt`} disabled={!canEdit} checked={t.status === 'done'}
                            onChange={(e) => setStatus.mutate({ item: t, status: e.currentTarget.checked ? 'done' : 'open' })}
                          />
                        </Table.Td>
                        <Table.Td><Anchor size="sm" fw={600} {...linkTo(navigate, `/projekte/${t.projectId}/termine`)}>{t.projectNumber}</Anchor></Table.Td>
                        <Table.Td><Text size="sm" ff="monospace">{t.code}</Text></Table.Td>
                        <Table.Td>
                          {canEdit ? (
                            <Group gap={4} wrap="nowrap" style={{ opacity: t.status === 'done' ? 0.6 : undefined }}>
                              {t.milestone && <Text size="sm" c="dimmed">◆</Text>}
                              <EditableText value={t.name} title="Titel ändern" onCommit={(name) => name.trim() && name !== t.name && edit.mutate({ item: t, body: { name: name.trim() } })} />
                            </Group>
                          ) : (
                            <Text size="sm" td={t.status === 'done' ? 'line-through' : undefined} c={t.status === 'done' ? 'dimmed' : undefined} lineClamp={2} title={t.name}>
                              {t.milestone ? '◆ ' : ''}{t.name}
                            </Text>
                          )}
                          {(t.group || t.deliverable) && <Text size="xs" c="dimmed" lineClamp={1}>{[t.group, t.deliverable && `Ergebnis: ${t.deliverable}`].filter(Boolean).join(' · ')}</Text>}
                          {missesDeadline(t) && (
                            // Why the task stands this far up: its "Spätestens" comes before its planned end.
                            <Text size="xs" c="red.7">Ende {formatDate(t.finish)} nach Spätestens {formatDate(t.deadline)} – Termin gefährdet</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {canEdit
                            ? <DateCell value={t.start} pinned={!!t.startFixed} title="Anfang frühestens festlegen" onCommit={(d) => d !== t.start && edit.mutate({ item: t, body: { startDate: d } })} />
                            : <Text size="sm">{formatDate(t.start)}</Text>}
                        </Table.Td>
                        <Table.Td ta="right">
                          {canEdit
                            ? <NumberCell value={t.durationDays} suffix={` ${UNIT_DAYS}`} title="Dauer in Arbeitstagen, 0 = Meilenstein" onCommit={(d) => d !== t.durationDays && edit.mutate({ item: t, body: { durationDays: d } })} />
                            : <Text size="sm">{t.durationDays} {UNIT_DAYS}</Text>}
                        </Table.Td>
                        <Table.Td>
                          {t.milestone ? null : canEdit
                            ? <DateCell value={t.finish} alert={late} title="Ende ändern (ändert die Dauer)" onCommit={(d) => d !== t.finish && edit.mutate({ item: t, body: { finishDate: d } })} />
                            : <Text size="sm" c={late ? 'red.7' : undefined} fw={late ? 700 : undefined}>{formatDate(t.finish)}</Text>}
                        </Table.Td>
                        <Table.Td>
                          {canEdit
                            ? <DateCell value={t.deadline} alert={(!!t.deadline && t.deadline < today && t.status !== 'done') || missesDeadline(t)} title="Spätestens (verschiebt nichts, warnt nur)" onCommit={(d) => d !== t.deadline && edit.mutate({ item: t, body: { deadline: d || null } })} />
                            : <Text size="sm" c={(t.deadline && t.deadline < today && t.status !== 'done') || missesDeadline(t) ? 'red.7' : 'dimmed'}>{t.deadline ? formatDate(t.deadline) : ''}</Text>}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            {canEdit
                              ? <EditableText value={t.owner ?? ''} allowEmpty placeholder="—" title="Verantwortliche (Kürzel)" onCommit={(owner) => (owner.trim() || null) !== t.owner && edit.mutate({ item: t, body: { owner: owner.trim() || null } })} />
                              : <Text size="sm">{t.owner}</Text>}
                            {t.critical && t.status !== 'done' && <Tooltip label="auf dem kritischen Pfad"><Badge size="xs" color="red" variant="light">krit.</Badge></Tooltip>}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Menu withinPortal position="bottom-start" disabled={!canEdit}>
                            <Menu.Target>
                              <Badge style={{ cursor: canEdit ? 'pointer' : undefined }} color={TASK_STATUS[t.status].color} variant="light">{TASK_STATUS[t.status].label}</Badge>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {(Object.keys(TASK_STATUS) as TaskStatus[]).map((k) => (
                                <Menu.Item key={k} disabled={k === t.status} onClick={() => setStatus.mutate({ item: t, status: k })}>{TASK_STATUS[k].label}</Menu.Item>
                              ))}
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Section>
        ))}
        {items.length === 0 && <Text c="dimmed">Keine Aufgaben für diese Auswahl.</Text>}
        {rowMenu.menu}
        <TaskEditModal target={editTarget} onClose={() => setEditTarget(null)} projects={data.data.map((p) => ({ projectId: p.projectId, label: `${p.number} – ${p.name}` }))} />
        {commentTask && <TaskCommentsModal projectId={commentTask.projectId} task={commentTask} onClose={() => setCommentTask(null)} />}
      </Stack>
    </div>
  );
}
